import { supabase } from "@/lib/supabase";

const ADMIN_PROFILE_COLS =
  "id, display_name, username, full_name, avatar_url, role, " +
  "is_deleted, is_suspended, is_verified_admin, created_at";

const ADMIN_EVENT_COLS =
  "id, title, category, city, date_time, cover_url, host_id, " +
  "is_deleted, is_hidden, is_featured, created_at";

// ---------- Stats ----------
async function countRows(table, filters = {}) {
  let q = supabase.from(table).select("id", { count: "exact", head: true });
  for (const [k, v] of Object.entries(filters)) q = q.eq(k, v);
  const { count, error } = await q;
  if (error) throw error;
  return count || 0;
}

export async function getAdminStats() {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const now = new Date().toISOString();

  const [users, events, reports, todaySignups, activeEvents] = await Promise.all([
    countRows("profiles"),
    countRows("events"),
    countRows("reports", { status: "open" }),
    supabase.from("profiles").select("id", { count: "exact", head: true })
      .gte("created_at", startOfToday.toISOString())
      .then(({ count }) => count || 0),
    supabase.from("events").select("id", { count: "exact", head: true })
      .gte("date_time", now)
      .eq("is_deleted", false)
      .eq("is_hidden", false)
      .then(({ count }) => count || 0),
  ]);
  return { users, events, reports, todaySignups, activeEvents };
}

export async function listLatest(table, limit = 5) {
  const cols = table === "profiles" ? ADMIN_PROFILE_COLS : ADMIN_EVENT_COLS;
  const { data, error } = await supabase
    .from(table)
    .select(cols)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

// ---------- Users ----------
export async function listUsers({ q = "", page = 0, pageSize = 25 } = {}) {
  let query = supabase
    .from("profiles")
    .select(ADMIN_PROFILE_COLS, { count: "exact" })
    .order("created_at", { ascending: false })
    .range(page * pageSize, page * pageSize + pageSize - 1);
  if (q) {
    const term = `%${q.toLowerCase()}%`;
    query = query.or(`username.ilike.${term},display_name.ilike.${term},full_name.ilike.${term}`);
  }
  const { data, error, count } = await query;
  if (error) throw error;

  // Hosted-count enrichment (one query, then group)
  const ids = (data || []).map((u) => u.id);
  let hostedMap = new Map();
  if (ids.length) {
    const { data: events } = await supabase
      .from("events")
      .select("host_id")
      .in("host_id", ids)
      .eq("is_deleted", false);
    for (const e of events || []) {
      hostedMap.set(e.host_id, (hostedMap.get(e.host_id) || 0) + 1);
    }
  }
  return {
    rows: (data || []).map((u) => ({ ...u, hosted_count: hostedMap.get(u.id) || 0 })),
    total: count || 0,
  };
}

export async function updateUserModeration(userId, patch) {
  const { error } = await supabase.from("profiles").update(patch).eq("id", userId);
  if (error) throw error;
}

// ---------- Events ----------
export async function listAdminEvents({ q = "", filter = "all", page = 0, pageSize = 25 } = {}) {
  let query = supabase
    .from("events")
    .select(
      `${ADMIN_EVENT_COLS}, host:profiles!events_host_id_fkey(display_name, username, avatar_url)`,
      { count: "exact" }
    )
    .order("created_at", { ascending: false })
    .range(page * pageSize, page * pageSize + pageSize - 1);

  if (q) {
    const term = `%${q.toLowerCase()}%`;
    query = query.or(`title.ilike.${term},city.ilike.${term}`);
  }
  if (filter === "hidden") query = query.eq("is_hidden", true);
  else if (filter === "deleted") query = query.eq("is_deleted", true);
  else if (filter === "featured") query = query.eq("is_featured", true);

  const { data, error, count } = await query;
  if (error) throw error;

  // Attendee counts
  const ids = (data || []).map((e) => e.id);
  const attMap = new Map();
  if (ids.length) {
    const { data: rsvps } = await supabase
      .from("rsvps")
      .select("event_id")
      .in("event_id", ids)
      .eq("status", "approved");
    for (const r of rsvps || []) attMap.set(r.event_id, (attMap.get(r.event_id) || 0) + 1);
  }

  return {
    rows: (data || []).map((e) => ({
      ...e,
      attendees_count: attMap.get(e.id) || 0,
      host_name: e.host?.display_name || "anon",
      host_username: e.host?.username || null,
      host_avatar_url: e.host?.avatar_url || null,
    })),
    total: count || 0,
  };
}

export async function updateEventModeration(eventId, patch) {
  const { error } = await supabase.from("events").update(patch).eq("id", eventId);
  if (error) throw error;
}

// ---------- Reports ----------
const REPORT_COLS =
  "id, reporter_id, target_type, target_id, reason, details, status, resolved_at, created_at";

export async function listReports({ status = "open", page = 0, pageSize = 25 } = {}) {
  let q = supabase
    .from("reports")
    .select(REPORT_COLS, { count: "exact" })
    .order("created_at", { ascending: false })
    .range(page * pageSize, page * pageSize + pageSize - 1);
  if (status !== "all") q = q.eq("status", status);

  const { data, error, count } = await q;
  if (error) throw error;
  const rows = data || [];

  // Enrich reporter + target profiles / event titles in two batched queries.
  const reporterIds = [...new Set(rows.map((r) => r.reporter_id).filter(Boolean))];
  const eventIds = rows.filter((r) => r.target_type === "event").map((r) => r.target_id);
  const profileIds = rows.filter((r) => r.target_type === "profile").map((r) => r.target_id);

  const [reporters, targetEvents, targetProfiles] = await Promise.all([
    reporterIds.length
      ? supabase.from("profiles").select("id, display_name, username, avatar_url").in("id", reporterIds).then(({ data }) => data || [])
      : Promise.resolve([]),
    eventIds.length
      ? supabase.from("events").select("id, title, city").in("id", eventIds).then(({ data }) => data || [])
      : Promise.resolve([]),
    profileIds.length
      ? supabase.from("profiles").select("id, display_name, username, avatar_url").in("id", profileIds).then(({ data }) => data || [])
      : Promise.resolve([]),
  ]);
  const rMap = new Map(reporters.map((p) => [p.id, p]));
  const eMap = new Map(targetEvents.map((e) => [e.id, e]));
  const pMap = new Map(targetProfiles.map((p) => [p.id, p]));

  return {
    rows: rows.map((r) => ({
      ...r,
      reporter: rMap.get(r.reporter_id) || null,
      target_event: r.target_type === "event" ? eMap.get(r.target_id) || null : null,
      target_profile: r.target_type === "profile" ? pMap.get(r.target_id) || null : null,
    })),
    total: count || 0,
  };
}

export async function resolveReport(reportId, userId) {
  const { error } = await supabase
    .from("reports")
    .update({ status: "resolved", resolved_at: new Date().toISOString(), resolved_by: userId })
    .eq("id", reportId);
  if (error) throw error;
}
